import React from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, CheckIcon, PlayIcon, FileTextIcon, LayoutTemplateIcon, WorkflowIcon, TrendingUpIcon } from 'lucide-react';
import { CDIsotype } from './BrandMark';
const ease = [0.22, 1, 0.36, 1] as const;
type FloatProps = {
  children: React.ReactNode;
  className?: string;
  /** seconds for one full float cycle */
  duration?: number;
  delay?: number;
  distance?: number;
  appearDelay?: number;
  style?: React.CSSProperties;
};
function Float({
  children,
  className = '',
  duration = 9,
  delay = 0,
  distance = 10,
  appearDelay = 0,
  style
}: FloatProps) {
  return <motion.div className={`absolute ${className}`} style={style} initial={{
    opacity: 0,
    y: 18,
    scale: 0.96
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} transition={{
    duration: 0.9,
    delay: appearDelay,
    ease
  }} data-id="element-119" data-mp="pn6o8sk2y11u">
      <motion.div animate={{
      y: [0, -distance, 0]
    }} transition={{
      duration,
      delay,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-120" data-mp="2f1utm9u96iqd">
        {children}
      </motion.div>
    </motion.div>;
}
function GlassPanel({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`rounded-2xl border border-white/[0.12] bg-white/[0.045] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.9)] backdrop-blur-md ${className}`} data-id="element-121" data-mp="1nt6xyqol42i9">
      {children}
    </div>;
}
function Bar({
  w,
  dim = false
}: {
  w: string;
  dim?: boolean;
}) {
  return <span className={`block h-[3px] rounded-full ${dim ? 'bg-white/12' : 'bg-white/25'}`} style={{
    width: w
  }} data-id="element-122" data-mp="2lgfes8dn4ndx" />;
}
function Sparkline({
  className = ''
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 120 44" preserveAspectRatio="none" className={className} aria-hidden="true" data-id="element-123" data-mp="2bot72m0ih6fx">
      <defs data-id="element-124" data-mp="2zdlufu32i5yx">
        <linearGradient id="sparkStroke" x1="0" y1="0" x2="1" y2="0" data-id="element-125" data-mp="2zdvktis0yf55">
          <stop offset="0%" stopColor="#2563FF" data-id="element-126" data-mp="347zwugg5ddd3" />
          <stop offset="100%" stopColor="#8B5CF6" data-id="element-127" data-mp="3e25hlhzz6pd3" />
        </linearGradient>
        <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1" data-id="element-128" data-mp="3e01bquiinga6">
          <stop offset="0%" stopColor="#4f6dff" stopOpacity="0.35" data-id="element-129" data-mp="393u3gv2ndsp2" />
          <stop offset="100%" stopColor="#6d7dff" stopOpacity="0" data-id="element-130" data-mp="343g5y6yoa15a" />
        </linearGradient>
      </defs>
      <path d="M0 34 L18 28 L34 31 L52 18 L70 22 L88 10 L104 14 L120 4 L120 44 L0 44 Z" fill="url(#sparkFill)" data-id="element-131" data-mp="301kuwaqxw9eb" />
      <path d="M0 34 L18 28 L34 31 L52 18 L70 22 L88 10 L104 14 L120 4" fill="none" stroke="url(#sparkStroke)" strokeWidth="1.6" strokeLinecap="round" data-id="element-132" data-mp="2v4ugrsyp3nvj" />
    </svg>;
}
function Columns() {
  const heights = [38, 62, 46, 78, 54, 92, 70];
  return <div className="flex h-full items-end gap-[6%]" data-id="element-133" data-mp="2gggg9oor4bi5">
      {heights.map((h, i) => <span key={i} className="flex-1 rounded-t-[2px]" style={{
      height: `${h}%`,
      background: i % 3 === 2 ? 'linear-gradient(180deg, #8b5cf6, rgba(139,92,246,0.15))' : 'linear-gradient(180deg, rgba(37,99,246,0.85), rgba(37,99,246,0.12))'
    }} data-id="element-134" data-mp="2ccifl9js2smz" data-mpi={i} />)}
    </div>;
}

/* ---------------- devices ---------------- */

function LaptopDashboard() {
  return <div className="w-full" data-id="element-135" data-mp="10vxp0q3wi7kn">
      <div className="rounded-[14px] border border-white/[0.14] bg-[#0f0f14]/90 p-[6px] shadow-[0_40px_90px_-30px_rgba(0,0,0,1)] backdrop-blur-md" data-id="element-136" data-mp="10v5u7hi4odvf">
        <div className="relative overflow-hidden rounded-[9px] border border-white/[0.08] bg-[#0b0b0f]" data-id="element-137" data-mp="86qndi95h1v6">
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-80" style={{
          background: 'radial-gradient(90% 120% at 85% 0%, rgba(37,99,246,0.20), transparent 60%), radial-gradient(70% 100% at 5% 100%, rgba(139,92,246,0.18), transparent 65%)'
        }} data-id="element-138" data-mp="d5l5sj37goer" />
          <div className="relative flex aspect-[16/10]" data-id="element-139" data-mp="3ljk1djm9m7rq">
            <div className="hidden w-[20%] flex-col gap-2 border-r border-white/[0.06] p-3 sm:flex" data-id="element-140" data-mp="1s48vrpx9gtp8">
              <div className="flex items-center gap-1.5" data-id="element-141" data-mp="1n5e5k2wys2v3">
                <CDIsotype className="h-3 w-3" data-id="element-142" data-mp="20v99gmgybz1y" />
                <Bar w="60%" data-id="element-143" data-mp="20x2i27b4ktcl" />
              </div>
              <div className="mt-2 space-y-2" data-id="element-144" data-mp="1w519owh2vmru">
                <Bar w="80%" data-id="element-145" data-mp="1w381hcqtguhq" />
                <Bar w="60%" dim data-id="element-146" data-mp="1w2y7le2ni36r" />
                <Bar w="70%" dim data-id="element-147" data-mp="2asw2sdyzzt5g" />
                <Bar w="50%" dim data-id="element-148" data-mp="2as46t21hnten" />
              </div>
            </div>
            <div className="flex-1 p-3" data-id="element-149" data-mp="25vdu8oow7fxz">
              <div className="flex items-center justify-between" data-id="element-150" data-mp="25wouwfn1k7kv">
                <div className="space-y-1.5" data-id="element-151" data-mp="2jtc0f6t2862e">
                  <Bar w="54px" data-id="element-152" data-mp="2js105gytpgg1" />
                  <Bar w="34px" dim data-id="element-153" data-mp="2fsif8l7055d7" />
                </div>
                <span className="rounded-full border border-white/[0.12] bg-white/[0.06] px-2 py-[3px] text-[6px] font-medium text-white/70" data-id="element-154" data-mp="2fqxluywfbsgx">
                  Últimos 30 días
                </span>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2" data-id="element-155" data-mp="bb2f3le765vs">
                {['+38%', '2.4k', '4.9'].map(k => <div key={k} className="rounded-lg border border-white/[0.08] bg-white/[0.03] p-2" data-id="element-156" data-mp="fcxhyubs0rbo">
                    <p className="text-[9px] font-semibold leading-none text-white" data-id="element-157" data-mp="1j6fu70dk9pz">
                      {k}
                    </p>
                    <span className="mt-1.5 block h-[3px] w-[60%] rounded-full bg-white/12" data-id="element-158" data-mp="3nrph6myi0k2k" />
                  </div>)}
              </div>
              <div className="mt-2 grid grid-cols-[1.5fr_1fr] gap-2" data-id="element-159" data-mp="3sp7ibr69xv00">
                <div className="rounded-lg border border-white/[0.08] bg-white/[0.03] p-2" data-id="element-160" data-mp="3esuc5e76xg0u">
                  <Bar w="40%" dim data-id="element-161" data-mp="3jrnjeovvwask" />
                  <Sparkline className="mt-1 h-[46px] w-full" data-id="element-162" data-mp="3jsq8cr9nc35u" />
                </div>
                <div className="rounded-lg border border-white/[0.08] bg-white/[0.03] p-2" data-id="element-163" data-mp="1d0xqhbrb6psc">
                  <Bar w="55%" dim data-id="element-164" data-mp="1hyqy8989fruq" />
                  <div className="mt-1 h-[46px]" data-id="element-165" data-mp="1fkm39184as7u">
                    <Columns data-id="element-166" data-mp="1fldlzbgm8vgp" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mx-auto h-[7px] w-[104%] -translate-x-[2%] rounded-b-[10px] border-x border-b border-white/[0.10] bg-gradient-to-b from-[#1a1a20] to-[#0e0e12]" data-id="element-167" data-mp="16jw69u74evsj" />
    </div>;
}
function TabletEbook() {
  return <div className="w-full rounded-[16px] border border-white/[0.14] bg-[#101017]/90 p-[5px] shadow-[0_30px_70px_-26px_rgba(0,0,0,0.95)] backdrop-blur-md" data-id="element-168" data-mp="1z939h6his9k4">
      <div className="relative overflow-hidden rounded-[12px] border border-white/[0.08] bg-[#0b0b10]" data-id="element-169" data-mp="1ubl5luid0kiz">
        <div aria-hidden="true" className="absolute inset-0" style={{
        background: 'radial-gradient(100% 80% at 50% 0%, rgba(139,92,246,0.28), transparent 65%)'
      }} data-id="element-170" data-mp="1pgfaq66zbvir" />
        <div className="relative flex aspect-[3/4] flex-col justify-between p-3" data-id="element-171" data-mp="2d82tu1y022ah">
          <div className="flex items-center justify-between" data-id="element-172" data-mp="2kjr319vo6fht">
            <span className="rounded-full border border-white/15 bg-white/[0.06] px-2 py-[2px] text-[6px] font-medium tracking-wide text-white/70" data-id="element-173" data-mp="2klln6lz4a3ku">
              eBook
            </span>
            <CDIsotype className="h-4 w-4" data-id="element-174" data-mp="2ugdjlmgyqke1" />
          </div>
          <div className="flex flex-col items-center" data-id="element-175" data-mp="2zh1lj36qcln1">
            <CDIsotype className="w-[56%]" data-id="element-176" data-mp="2zeowk62k1b0h" />
            <svg viewBox="0 0 100 24" preserveAspectRatio="none" className="mt-2 h-5 w-[80%] opacity-80" aria-hidden="true" data-id="element-177" data-mp="33j4wzrtefq8b">
              <defs data-id="element-178" data-mp="3d7wlfpz93seh">
                <linearGradient id="ebookWave" x1="0" y1="0" x2="1" y2="0" data-id="element-179" data-mp="3d9hgrhte3fde">
                  <stop offset="0%" stopColor="#2563FF" data-id="element-180" data-mp="3i7iz20izrant" />
                  <stop offset="100%" stopColor="#8B5CF6" data-id="element-181" data-mp="3i93tlq5b2tlo" />
                </linearGradient>
              </defs>
              {[0, 4].map(o => <path key={o} d={`M0 ${14 + o} C 24 ${4 + o}, 40 ${20 + o}, 64 ${10 + o} S 88 ${2 + o}, 100 ${8 + o}`} fill="none" stroke="url(#ebookWave)" strokeWidth="1" strokeOpacity={o === 0 ? 0.9 : 0.4} data-id="element-182" data-mp="3r8za8ahmrgt3" />)}
            </svg>
          </div>
          <div className="space-y-1.5" data-id="element-183" data-mp="lc3726mlk79d">
            <Bar w="78%" data-id="element-184" data-mp="ld4gk6nysloz" />
            <Bar w="52%" dim data-id="element-185" data-mp="las2w5p8oxhq" />
            <div className="mt-3 flex items-center justify-between" data-id="element-186" data-mp="3trva2oo3geve">
              <span className="text-[6.5px] font-light text-white/45" data-id="element-187" data-mp="3tq1xytupp2g0">
                Capítulo 03 / 12
              </span>
              <span className="h-[3px] w-[38%] overflow-hidden rounded-full bg-white/10" data-id="element-188" data-mp="7dbvorz4czox">
                <span className="block h-full w-[45%] rounded-full brand-gradient-bg" data-id="element-189" data-mp="2mdb3g73lffv" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function PhoneUI() {
  return <div className="w-full rounded-[16px] border border-white/[0.16] bg-[#101017]/95 p-[4px] shadow-[0_30px_60px_-22px_rgba(0,0,0,1)] backdrop-blur-md" data-id="element-190" data-mp="363c1s951o23b">
      <div className="relative overflow-hidden rounded-[13px] border border-white/[0.08] bg-[#0b0b10]" data-id="element-191" data-mp="1liopr8c96h9s">
        <div aria-hidden="true" className="absolute inset-0" style={{
        background: 'radial-gradient(90% 60% at 50% 0%, rgba(37,99,246,0.30), transparent 70%)'
      }} data-id="element-192" data-mp="1qdksb2f48n15" />
        <div className="relative flex aspect-[9/19] flex-col gap-2 p-2" data-id="element-193" data-mp="24are75v4z1ul">
          <span className="mx-auto h-[3px] w-[28%] rounded-full bg-white/20" data-id="element-194" data-mp="29adnx5br9zyn" />
          <div className="flex items-center gap-1.5" data-id="element-195" data-mp="2989mcu1bxd1j">
            <CDIsotype className="h-3 w-3 shrink-0" data-id="element-196" data-mp="1vbe368rd657r" />
            <div className="flex-1 space-y-1" data-id="element-197" data-mp="1vcxfxpdt9y4s">
              <Bar w="70%" data-id="element-198" data-mp="208mky88lo78h" />
              <Bar w="44%" dim data-id="element-199" data-mp="207uo6u39o3gm" />
            </div>
          </div>
          <div className="rounded-lg border border-white/[0.08] bg-white/[0.04] p-1.5" data-id="element-200" data-mp="jv62ydzen5z9">
            <Sparkline className="h-[22px] w-full" data-id="element-201" data-mp="ov21hxpc257f" />
          </div>
          <div className="grid grid-cols-2 gap-1.5" data-id="element-202" data-mp="os62s75nvchx">
            {[0, 1, 2, 3].map(i => <div key={i} className="rounded-lg border border-white/[0.07] bg-white/[0.03] p-1.5" data-id="element-203" data-mp="yl6o7g76k7qu">
                <span className="block h-2.5 w-2.5 rounded-[3px]" style={{
              background: i % 2 === 0 ? 'linear-gradient(135deg,#2563F6,#5b8cff)' : 'linear-gradient(135deg,#8b5cf6,#c4b5fd)'
            }} data-id="element-204" data-mp="ory8g7ewk3zy" />
                <span className="mt-1.5 block h-[3px] w-[70%] rounded-full bg-white/15" data-id="element-205" data-mp="jrk00o9e2s1e" />
              </div>)}
          </div>
          <div className="mt-auto rounded-full bg-white/90 py-1 text-center text-[6px] font-semibold text-[#0d0d0f]" data-id="element-206" data-mp="asgfrgyhk6lb">
            Descargar
          </div>
        </div>
      </div>
    </div>;
}
function BrowserAnalytics() {
  return <div className="w-full overflow-hidden rounded-[14px] border border-white/[0.12] bg-[#0f0f14]/80 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)] backdrop-blur-md" data-id="element-207" data-mp="256hnkrc0k3xj">
      <div className="flex items-center gap-1.5 border-b border-white/[0.07] px-3 py-2" data-id="element-208" data-mp="2acwb0966g9d7">
        <span className="h-1.5 w-1.5 rounded-full bg-white/20" data-id="element-209" data-mp="2o418p5pzirkw" />
        <span className="h-1.5 w-1.5 rounded-full bg-white/15" data-id="element-210" data-mp="2o5chjjyaepiv" />
        <span className="h-1.5 w-1.5 rounded-full bg-white/10" data-id="element-211" data-mp="2jb87usxn03e3" />
        <span className="ml-2 flex h-3.5 flex-1 items-center gap-1 rounded-full border border-white/[0.06] bg-white/[0.03] px-1.5" data-id="element-212" data-mp="2y15ydfj1h56g">
          <CDIsotype className="h-2 w-2" data-id="element-213" data-mp="2t22uvzipj70l" />
          <span className="text-[5px] font-light text-white/40" data-id="element-214" data-mp="372dkkz8a6fk6">
            creativodigital.shop
          </span>
        </span>
      </div>
      <div className="p-3" data-id="element-215" data-mp="331tqy5mx1u2s">
        <div className="flex items-end justify-between" data-id="element-216" data-mp="331jwa4qxeyqr">
          <div className="space-y-1" data-id="element-217" data-mp="1cnku1em98ez3">
            <span className="text-[7px] font-medium uppercase tracking-[0.14em] text-white/40" data-id="element-218" data-mp="1comf8bwzihuj">
              Analytics
            </span>
            <p className="text-[13px] font-semibold leading-none text-white" data-id="element-219" data-mp="37rr7ulmudgc2">
              +214%
            </p>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full border border-[#8b5cf6]/35 bg-[#8b5cf6]/12 px-2 py-[3px] text-[6.5px] font-medium text-[#b49cff]" data-id="element-220" data-mp="3crn9ie82gvog">
            <TrendingUpIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-221" data-mp="3moibj73jte7h" />
            ROAS 4.8x
          </span>
        </div>
        <Sparkline className="mt-2 h-[52px] w-full" data-id="element-222" data-mp="3qpbxhuxfkixq" />
        <div className="mt-2 grid grid-cols-3 gap-1.5" data-id="element-223" data-mp="3qoivt8dbwjmg">
          {['Meta', 'Google', 'Orgánico'].map(s => <div key={s} className="rounded-md border border-white/[0.07] bg-white/[0.03] px-1.5 py-1" data-id="element-224" data-mp="4b2b1rd29kzl">
              <p className="text-[6px] font-light text-white/45" data-id="element-225" data-mp="98kcyxsnuzy3">{s}</p>
              <span className="mt-1 block h-[3px] w-full overflow-hidden rounded-full bg-white/10" data-id="element-226" data-mp="e9rieq7bzn1l">
                <span className="block h-full rounded-full brand-gradient-bg" style={{
              width: s === 'Meta' ? '78%' : s === 'Google' ? '54%' : '38%'
            }} data-id="element-227" data-mp="i9s8x97pfop0" />
              </span>
            </div>)}
        </div>
      </div>
    </div>;
}

/* ---------------- floating cards ---------------- */

function PromptCard() {
  return <GlassPanel className="w-full p-3" data-id="element-228" data-mp="1otcsnfiwr2z6">
      <div className="flex items-center gap-2" data-id="element-229" data-mp="1oue3pjzxbpgw">
        <span className="grid h-5 w-5 place-items-center rounded-lg brand-gradient-bg" data-id="element-230" data-mp="2mgbu03kjoz3e">
          <SparklesIcon className="h-3 w-3 text-white" aria-hidden="true" data-id="element-231" data-mp="2hhide29yorz0" />
        </span>
        <span className="text-[8px] font-medium tracking-wide text-white/75" data-id="element-232" data-mp="2hjutsb02mu9y">
          AI Prompt
        </span>
        <span className="ml-auto text-[6.5px] font-light text-white/35" data-id="element-233" data-mp="27rp2l3hn8gvt">
          GPT · Claude
        </span>
      </div>
      <p className="mt-2.5 text-[8px] font-light leading-relaxed text-white/55" data-id="element-234" data-mp="357cpuiqdceiq">
        “Generá 30 días de contenido para una clínica de estética con foco en
        conversión…”
      </p>
      <div className="mt-2.5 flex gap-1.5" data-id="element-235" data-mp="2kxtp6vnqe2gn">
        {['Contenido', 'Ads', 'Email'].map(t => <span key={t} className="rounded-full border border-white/[0.10] bg-white/[0.04] px-2 py-[2px] text-[6px] font-light text-white/50" data-id="element-236" data-mp="2p0g8g86c6f28">
            {t}
          </span>)}
      </div>
    </GlassPanel>;
}
function WorkflowCard() {
  const steps = ['Lead', 'IA', 'Email', 'Venta'];
  return <GlassPanel className="w-full p-3" data-id="element-237" data-mp="33pcrt1dufei5">
      <div className="flex items-center gap-1.5" data-id="element-238" data-mp="38nxq2v9jbo3y">
        <WorkflowIcon className="h-3 w-3 text-[#7fa3ff]" strokeWidth={1.5} aria-hidden="true" data-id="element-239" data-mp="38mmewadrfe2t" />
        <span className="text-[8px] font-medium text-white/75" data-id="element-240" data-mp="2tx6zfqjoiv2f">
          Automatización
        </span>
      </div>
      <div className="mt-2.5 flex items-center gap-1" data-id="element-241" data-mp="3mnoxue9gk89i">
        {steps.map((s, i) => <React.Fragment key={s} data-id="element-242" data-mp="3mlvvhb5voa7b" data-mpi={i}>
            <span className="rounded-md border border-white/[0.10] bg-white/[0.05] px-1.5 py-[3px] text-[6px] font-light text-white/60" data-id="element-243" data-mp="3reovxmubcwdl">
              {s}
            </span>
            {i < steps.length - 1 && <span className="h-[1px] w-2 brand-gradient-bg" data-id="element-244" data-mp="1azk633jjj12m" />}
          </React.Fragment>)}
      </div>
    </GlassPanel>;
}
function ChecklistCard() {
  return <GlassPanel className="w-full p-3" data-id="element-245" data-mp="1kpwooq7sok64">
      <div className="flex items-center justify-between" data-id="element-246" data-mp="1kqy93lapaj0i">
        <span className="text-[8px] font-medium text-white/75" data-id="element-247" data-mp="wzsoksttf2jv">Checklist</span>
        <span className="text-[6.5px] font-light text-white/35" data-id="element-248" data-mp="s1r5i7we338e">3/4</span>
      </div>
      <ul className="mt-2 space-y-1.5" data-id="element-249" data-mp="1618xsx8w8ymi">
        {['Oferta definida', 'Embudo activo', 'Contenido listo', 'Ads en test'].map((item, i) => <li key={item} className="flex items-center gap-1.5" data-id="element-250" data-mp="110m9ddbyo18j" data-mpi={i}>
              <span className={`grid h-3 w-3 place-items-center rounded-[4px] border ${i < 3 ? 'border-transparent brand-gradient-bg' : 'border-white/15 bg-white/[0.04]'}`} data-id="element-251" data-mp="2hfw4n5q9ykyq">
                {i < 3 && <CheckIcon className="h-2 w-2 text-white" strokeWidth={3} aria-hidden="true" data-id="element-252" data-mp="1f1yd7anz06ch" />}
              </span>
              <span className="text-[7px] font-light text-white/55" data-id="element-253" data-mp="1o2efigsz97fr">{item}</span>
            </li>)}
      </ul>
    </GlassPanel>;
}
function LessonCard() {
  return <GlassPanel className="w-full p-2.5" data-id="element-254" data-mp="vg20960svwmc">
      <div className="flex items-center gap-2" data-id="element-255" data-mp="vcxptqwqs4n8">
        <span className="grid h-6 w-6 place-items-center rounded-lg border border-white/[0.10] bg-white/[0.05]" data-id="element-256" data-mp="10dv3bq6t4aie">
          <PlayIcon className="h-2.5 w-2.5 fill-[#b49cff] text-[#b49cff]" aria-hidden="true" data-id="element-257" data-mp="155lkaf091wis" />
        </span>
        <div className="flex-1" data-id="element-258" data-mp="7nlgmr1ezwkx">
          <p className="text-[7.5px] font-medium text-white/75" data-id="element-259" data-mp="co0wbp965g62">
            Lección 04 · Embudos
          </p>
          <span className="mt-1 block h-[3px] w-full overflow-hidden rounded-full bg-white/10" data-id="element-260" data-mp="53ykv8cnfg57">
            <span className="block h-full w-[62%] rounded-full brand-gradient-bg" data-id="element-261" data-mp="6yf6yjpofem" />
          </span>
        </div>
      </div>
    </GlassPanel>;
}
function AssetsCard() {
  return <GlassPanel className="w-full p-2.5" data-id="element-262" data-mp="3nb5vtgk2r18j">
      <div className="flex items-center gap-1.5" data-id="element-263" data-mp="3na34j7iuaws3">
        <LayoutTemplateIcon className="h-3 w-3 text-[#7fa3ff]" strokeWidth={1.5} aria-hidden="true" data-id="element-264" data-mp="3ib9uxq6obpx7" />
        <span className="text-[7.5px] font-medium text-white/75" data-id="element-265" data-mp="3dj8jg6hby388">
          Plantillas
        </span>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-1" data-id="element-266" data-mp="38lzxrvwz3h28">
        {[0, 1, 2, 3, 4, 5].map(i => <span key={i} className="block aspect-square rounded-[5px] border border-white/[0.08]" style={{
        background: i % 3 === 0 ? 'linear-gradient(135deg, rgba(37,99,246,0.55), rgba(37,99,246,0.10))' : i % 3 === 1 ? 'linear-gradient(135deg, rgba(139,92,246,0.5), rgba(139,92,246,0.08))' : 'rgba(255,255,255,0.05)'
      }} data-id="element-267" data-mp="33oglv2nqzgdg" />)}
      </div>
    </GlassPanel>;
}
function DocCard() {
  return <GlassPanel className="w-full p-2.5" data-id="element-268" data-mp="hdei5rb4pf54">
      <div className="flex items-center gap-1.5" data-id="element-269" data-mp="hccyiyg1rkbs">
        <FileTextIcon className="h-3 w-3 text-white/60" strokeWidth={1.5} aria-hidden="true" data-id="element-270" data-mp="3mht2agevg2t" />
        <span className="text-[7.5px] font-medium text-white/70" data-id="element-271" data-mp="8f2ikzsab70j">
          Guía PDF
        </span>
      </div>
      <div className="mt-2 space-y-1.5" data-id="element-272" data-mp="3pvssq8zio29d">
        <Bar w="90%" dim data-id="element-273" data-mp="3ussx8fgpwwyd" />
        <Bar w="72%" dim data-id="element-274" data-mp="3uu426ilwgar2" />
        <Bar w="80%" dim data-id="element-275" data-mp="3utc5f4gkg6z7" />
      </div>
    </GlassPanel>;
}

/* ---------------- ambience ---------------- */

const PARTICLES = [{
  x: 12,
  y: 18,
  d: 6,
  s: 3
}, {
  x: 82,
  y: 12,
  d: 7.5,
  s: 2
}, {
  x: 46,
  y: 6,
  d: 9,
  s: 2
}, {
  x: 92,
  y: 46,
  d: 8,
  s: 3
}, {
  x: 6,
  y: 58,
  d: 10,
  s: 2
}, {
  x: 68,
  y: 86,
  d: 7,
  s: 3
}, {
  x: 30,
  y: 92,
  d: 8.5,
  s: 2
}, {
  x: 58,
  y: 40,
  d: 11,
  s: 2
}];
function Ambience() {
  return <div aria-hidden="true" className="pointer-events-none absolute inset-0" data-id="element-276" data-mp="3b7am6qqqp2ug">
      <div className="absolute inset-[-12%] rounded-full blur-[110px]" style={{
      background: 'radial-gradient(45% 45% at 30% 28%, rgba(37,99,246,0.22), transparent 70%), radial-gradient(45% 45% at 72% 66%, rgba(139,92,246,0.22), transparent 70%)'
    }} data-id="element-277" data-mp="3b5685fz4mngg" />
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" data-id="element-278" data-mp="3g49cszb72rnw">
        <defs data-id="element-279" data-mp="3pxhwukai9hkl">
          <linearGradient id="linkGrad" x1="0" y1="0" x2="1" y2="1" data-id="element-280" data-mp="3pwov5xqeli9b">
            <stop offset="0%" stopColor="#2563FF" stopOpacity="0.5" data-id="element-281" data-mp="7rlow9opb07b" />
            <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.05" data-id="element-282" data-mp="2rqxm510k8lq" />
          </linearGradient>
        </defs>
        <g fill="none" stroke="url(#linkGrad)" strokeWidth="0.22" strokeDasharray="1.2 2.4" vectorEffect="non-scaling-stroke" data-id="element-283" data-mp="f9nzlxgvpxxa">
          <path d="M18 26 L44 48" data-id="element-284" data-mp="p84wfb2i16r9" />
          <path d="M78 20 L52 46" data-id="element-285" data-mp="p725j356f4bc" />
          <path d="M86 58 L54 52" data-id="element-286" data-mp="p6aiigpnaewo" />
          <path d="M16 78 L46 60" data-id="element-287" data-mp="k8sb4ura07qs" />
          <path d="M64 88 L54 60" data-id="element-288" data-mp="k9tlsy4dqs7z" />
        </g>
      </svg>
      {PARTICLES.map((p, i) => <motion.span key={i} className="absolute rounded-full bg-white" style={{
      left: `${p.x}%`,
      top: `${p.y}%`,
      height: p.s,
      width: p.s,
      boxShadow: '0 0 8px rgba(165,180,255,0.9)'
    }} animate={{
      opacity: [0.15, 0.75, 0.15],
      y: [0, -14, 0]
    }} transition={{
      duration: p.d,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: i * 0.6
    }} data-id="element-289" data-mp="3spvhxcm7xn3x" data-mpi={i} />)}
    </div>;
}

/* ---------------- composition ---------------- */

export function HeroEcosystem() {
  return <div className="relative mx-auto w-full max-w-[640px]" role="img" aria-label="Ecosistema de productos digitales inteligentes: dashboard en laptop, eBook en tablet, app móvil, tarjetas de IA, analítica, automatizaciones y plantillas flotando en capas" data-id="element-290" data-mp="1kfnjtk1z8oww">
      <div className="relative aspect-[1/1.02] w-full" data-id="element-291" data-mp="1finejbcybq6u">
        <Ambience data-id="element-292" data-mp="237q89hkv19hp" />

        {/* back layer */}
        <Float className="left-[1%] top-[3%] w-[56%]" duration={12} distance={8} appearDelay={0.1} style={{
        opacity: 0.92
      }} data-id="element-293" data-mp="235vjttadqz8v">
          <div style={{
          transform: 'perspective(900px) rotateY(6deg) rotateX(2deg)'
        }} data-id="element-294" data-mp="1u70e0ib8zr63">
            <BrowserAnalytics data-id="element-295" data-mp="1z4tin6wwkd49" />
          </div>
        </Float>

        <Float className="right-[0%] top-[24%] w-[27%]" duration={10.5} delay={0.8} distance={13} appearDelay={0.28} data-id="element-296" data-mp="irlgndf4mxag">
          <div style={{
          transform: 'perspective(900px) rotateY(-12deg) rotate(2deg)'
        }} data-id="element-297" data-mp="2pgdpufl7hjkk">
            <TabletEbook data-id="element-298" data-mp="2pi7zgxr11p9c" />
          </div>
        </Float>

        {/* mid layer cards */}
        <Float className="right-[2%] top-[1%] w-[40%]" duration={8.5} delay={0.4} distance={12} appearDelay={0.34} data-id="element-299" data-mp="2kjdje3kg2esx">
          <PromptCard data-id="element-300" data-mp="2boh1mmmnrttg" />
        </Float>

        <Float className="left-[0%] top-[36%] w-[36%]" duration={9.5} delay={1.1} distance={11} appearDelay={0.42} data-id="element-301" data-mp="26pvzuirrd22w">
          <WorkflowCard data-id="element-302" data-mp="21svuka2qg3cu" />
        </Float>

        <Float className="left-[42%] top-[26%] w-[22%]" duration={11} delay={0.2} distance={9} appearDelay={0.5} style={{
        opacity: 0.96
      }} data-id="element-303" data-mp="1wr6g0kgax7k0">
          <DocCard data-id="element-304" data-mp="3r60r2ltkxc9m" />
        </Float>

        <Float className="right-[1%] bottom-[6%] w-[30%]" duration={9} delay={0.9} distance={12} appearDelay={0.58} data-id="element-305" data-mp="3r4xz0akit3s4">
          <AssetsCard data-id="element-306" data-mp="18vlanht64ejl" />
        </Float>

        {/* front layer */}
        <Float className="left-[13%] bottom-[10%] w-[76%]" duration={13} delay={0.5} distance={7} appearDelay={0.2} data-id="element-307" data-mp="1dv7l5jhm3gop">
          <LaptopDashboard data-id="element-308" data-mp="1iok175886f5u" />
        </Float>

        <Float className="left-[-2%] top-[52%] w-[16%]" duration={8} delay={1.4} distance={14} appearDelay={0.46} data-id="element-309" data-mp="1mmq1buek5whs">
          <div style={{
          transform: 'rotate(-4deg)'
        }} data-id="element-310" data-mp="uwbxys4682kd">
            <PhoneUI data-id="element-311" data-mp="uwvcrzyiyau6" />
          </div>
        </Float>

        <Float className="left-[16%] bottom-[0%] w-[34%]" duration={10} delay={1.7} distance={10} appearDelay={0.64} data-id="element-312" data-mp="yxxeqojtt0zu">
          <ChecklistCard data-id="element-313" data-mp="2adm2hi4wm41p" />
        </Float>

        <Float className="right-[24%] top-[62%] w-[30%]" duration={8.8} delay={0.6} distance={11} appearDelay={0.7} data-id="element-314" data-mp="2ackfqgeizt45">
          <LessonCard data-id="element-315" data-mp="2duhvlt11emfq" />
        </Float>
      </div>
    </div>;
}